import { useEffect, useMemo, useState } from "react";
import { fetchGET } from "../../apiHelper";
import {
  projektnaPomocClaniceSearch,
  projektnaPomocPartnerjiSearch,
  projektnaPomocProjektiSearch,
} from "../../listOfApi";
import { mapProjectToSelectOption, mapToSelectOption } from "./deminimisUtils";

import { MODULES, ROLE_GROUPS } from "../../auth/roles";
import useRoleCheck from "../../auth/useRoleCheck";

const useDeminimisOptions = (role) => {
  const { isFakultetniSkrbnik, isAdmin } = useRoleCheck(MODULES.DEMINIMIS);

  //check if user is not admin
  const isNonAdminUser = !isAdmin;

  const userClanicaId = role?.clanicaID ?? role?.clanicaId;

  const defaultOptions = useMemo(() => [], []);
  const [prejemnikDefaultOptions, setPrejemnikDefaultOptions] = useState([]);
  const [dajalecDefaultOptions, setDajalecDefaultOptions] = useState([]);

  const getSearchOptions = async (
    apiUrl,
    searchTerm,
    oldOptions = [],
    mapOption = mapToSelectOption,
  ) => {
    if (!searchTerm || searchTerm.trim().length === 0) {
      return oldOptions;
    }

    const response = await fetchGET(
      `${apiUrl}?query=${encodeURIComponent(searchTerm.trim())}`,
    );

    if (response === 0 || !Array.isArray(response)) {
      return [];
    }

    return response.map(mapOption).filter((item) => item.name !== "");
  };

  const fetchPrejemnikOptions = (searchTerm, oldOptions) =>
    getSearchOptions(projektnaPomocClaniceSearch, searchTerm, oldOptions);

  const fetchDajalecOptions = (searchTerm, oldOptions) =>
    getSearchOptions(projektnaPomocPartnerjiSearch, searchTerm, oldOptions);

  const fetchProjektOptions = (clanicaId, searchTerm, oldOptions) => {
    // If no clanicaId is selected, don't fetch anything
    if (!clanicaId) {
      return Promise.resolve([]);
    }

    // Use empty string for query to get all projects, or the search term if provided
    const query =
      searchTerm && searchTerm.trim().length > 0 ? searchTerm.trim() : "";
    const queryParams = `query=${encodeURIComponent(query)}&clanicaID=${encodeURIComponent(clanicaId)}`;

    return fetchGET(`${projektnaPomocProjektiSearch}?${queryParams}`).then(
      (response) => {
        if (response === 0 || !Array.isArray(response)) {
          return [];
        }
        return response
          .map(mapProjectToSelectOption)
          .filter((item) => item.name !== "");
      },
    );
  };

  useEffect(() => {
    const loadInitialDropdownOptions = async () => {
      const [prejemnikResponse, dajalecResponse] = await Promise.all([
        fetchGET(`${projektnaPomocClaniceSearch}?query=`),
        fetchGET(`${projektnaPomocPartnerjiSearch}?query=`),
      ]);

      if (Array.isArray(prejemnikResponse)) {
        const mappedPrejemniki = prejemnikResponse
          .map(mapToSelectOption)
          .filter((item) => item.name !== "");

        if (
          (isNonAdminUser || isFakultetniSkrbnik) &&
          userClanicaId !== undefined &&
          userClanicaId !== null
        ) {
          const clanicaValue = String(userClanicaId);
          const hasUserClanicaOption = mappedPrejemniki.some(
            (item) => String(item.value) === clanicaValue,
          );

          if (hasUserClanicaOption) {
            setPrejemnikDefaultOptions(mappedPrejemniki);
          } else {
            const fallbackClanicaName = String(
              role?.clanica || role?.clanicaNaziv || "Moja članica",
            ).trim();

            setPrejemnikDefaultOptions([
              ...mappedPrejemniki,
              {
                name: fallbackClanicaName || clanicaValue,
                value: clanicaValue,
              },
            ]);
          }
        } else {
          setPrejemnikDefaultOptions(mappedPrejemniki);
        }
      }

      if (Array.isArray(dajalecResponse)) {
        setDajalecDefaultOptions(
          dajalecResponse
            .map(mapToSelectOption)
            .filter((item) => item.name !== ""),
        );
      }
    };

    loadInitialDropdownOptions();
  }, [
    isNonAdminUser,
    isFakultetniSkrbnik,
    role?.clanica,
    role?.clanicaNaziv,
    userClanicaId,
  ]);

  return {
    isNonAdminUser,
    userClanicaId,
    defaultOptions,
    prejemnikDefaultOptions,
    dajalecDefaultOptions,
    fetchPrejemnikOptions,
    fetchDajalecOptions,
    fetchProjektOptions,
  };
};

export default useDeminimisOptions;
